import { MouseEvent, useMemo, useState } from 'react'

import { Nullable } from '@types'

import CustomPopover from '@components/CustomPopover'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'

import { StyledMenuList, YearDropdownItemLabel, YearDropdownRoot } from './YearDropdown.styles'
import { fillYearsRange } from './utils'

interface YearDropdownProps {
  selectedYear: number
  years: number[]
  onYearSelect: (year: number) => void
  disabled: boolean
}

const YearDropdown = ({ selectedYear, years, onYearSelect, disabled }: YearDropdownProps) => {
  const [yearsAnchorEl, setYearsAnchorEl] = useState<Nullable<HTMLElement>>(null)

  const handleYearDropdownClick = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      return
    }

    setYearsAnchorEl(prev => (prev ? null : e.currentTarget))
  }

  const handleYearDropdownClose = () => {
    setYearsAnchorEl(null)
  }

  const yearsRange = useMemo(() => fillYearsRange(years), [years])

  return (
    <>
      <YearDropdownRoot $isClickable={!disabled} onClick={handleYearDropdownClick}>
        <Typography color="primary" variant="h2">
          {selectedYear}
        </Typography>
        {!disabled && <ExpandMoreIcon />}
      </YearDropdownRoot>
      <CustomPopover id="date-range-slider-year-dropdown" anchor={yearsAnchorEl} setAnchor={handleYearDropdownClose}>
        <StyledMenuList>
          {yearsRange.map(year => {
            const isSelected = selectedYear === year
            const isDisabled = !years.includes(year)

            return (
              <MenuItem
                key={year}
                sx={{ ...(isDisabled && { pointerEvents: 'none' }) }}
                onClick={() => {
                  handleYearDropdownClose()
                  onYearSelect(year)
                }}
              >
                <YearDropdownItemLabel variant="body1" $isSelected={isSelected} $isDisabled={isDisabled}>
                  {year}
                </YearDropdownItemLabel>
              </MenuItem>
            )
          })}
        </StyledMenuList>
      </CustomPopover>
    </>
  )
}

export default YearDropdown
